import { logError } from '../../utils/logError';
import $$ from 'dom7';

/**
 * @class implements a modal window with backdrop with
 * support for closing by clicking on 'ESC',
 * support for history,
 * support for opening a modal by hash provided in URL,
 * support for opening a modal by clicking on [data-prk-modal-target] or [href] elements,
 * onBeforeOpen, onOpen, onBeforeClose, onClose callbacks.
 * It also prevents duplication on PrkModals meading that it is safe to call new PrkModal()
 * several times on the same element
 */
export class PrkModal {

    static DEFAULT_OPTIONS = {
        closeOnEscape: true,
        closeOnBackdropClick: true,
        isPersistant: false,
        useHistory: true,
        onBeforeClose: null, // (next: Function) => { ... }, to continue, 'next' shall be called inside the hook
        onClose: null,
        onBeforeOpen: null, // (next: Function) => { ... }, to continue, 'next' shall be called inside the hook
        onOpen: null,
        renderCloseButton: true
    };

    static IS_HIDDEN_ATTRIBUTE = 'aria-hidden';
    static A_MODAL_IS_OPENED_DOCUMENT_CLASS = 'prk-modal-is-opened';
    static IS_INITIALIZED_CLASS = 'is-initialized';

    static ACTION_CLOSE = 'close';
    static ACTION_CONFIRM = 'confirm';
    static ACTION_CANCEL = 'cancel';
    static ACTION_ATTRIBUTE = 'data-prk-modal-action';
    static ACTION_DATASET = 'prkModalAction';

    static EVENT_CONFIRMATION = 'confirmation';
    static EVENT_CANCELLATION = 'cancellation';

    static CLOSE_BUTTON_CLASS = 'prk-modal-close-button';
    static CLOSE_ICON = '<svg class="pi pi-close"><use xlink:href="#close"></use></svg>';

    static GENERATED_ID = 'prk-modal';
    static genericIdCounter = 0;

    static list = {}; // List of currently initialized modals to prevent duplication via constructor
    static overlay = null; // One overlay for all the modals

    /**
     * @param {HTMLElement} modal
     * @param {{
     *  closeOnEscape?: boolean,
     *  closeOnBackdropClick?: boolean,
     *  isPersistant?: boolean,
     *  useHistory?: boolean,
     *  onBeforeClose?: null|(next: Function) => void, to continue, 'next' shall be called inside the hook
     *  onClose?: null|Function,
     *  onBeforeOpen?: null|(next: Function) => void, to continue, 'next' shall be called inside the hook
     *  onOpen?: null|Function,
     *  renderCloseButton?: boolean
     * }|undefined} options
     */
    constructor (modal, options) {
        if (!(modal instanceof HTMLElement)) return;

        this.modal = modal;
        this.options = Object.assign({}, PrkModal.DEFAULT_OPTIONS, options);

        // Saving bound to this event listeners
        // is a must in case these listeners can be detached
        this.eventListeners = {
            onKeyPress: this.onKeyPress.bind(this),
            onHistoryPopState: this.onHistoryPopState.bind(this),
            onInsideModalClick: this.onInsideModalClick.bind(this)
        };

        // Create an overlay
        if (!PrkModal.overlay) {
            PrkModal.overlay = document.createElement('div');
            PrkModal.overlay.className = 'prk-modal-overlay';
            document.body.appendChild(PrkModal.overlay);
        }

        // Generate id if no provided
        if (!this.id) {
            this.id = `${PrkModal.GENERATED_ID}-${PrkModal.genericIdCounter++}`;
        }

        if (this.isInitialized) {

            // Condition should always be true (in case of this.isInitialized === true)
            // but just in case to prevent access errors
            if (PrkModal.list[this.id]) {
                return PrkModal.list[this.id];
            }
        } else {

            // Condition should always be true (in case of this.isInitialized === false)
            // but just in case to prevent access errors
            if (!PrkModal.list[this.id])
                PrkModal.list[this.id] = this;

            // try parentElement as a wrap
            this.wrap = this.modal.parentElement;

            // If modal is not properly wrapped
            if (!this.wrap.classList.contains('prk-modal-wrap')) {
                const wrap = document.createElement('div');
                wrap.className = 'prk-modal-wrap';
                wrap.appendChild(this.modal);
                this.wrap.appendChild(wrap);
                this.wrap = wrap;
            }

            if (this.options.renderCloseButton) {
                let closeButton = this.modal.querySelector(`.${PrkModal.CLOSE_BUTTON_CLASS}`);
                if (!closeButton) {
                    closeButton = document.createElement('button');
                    closeButton.setAttribute('type', 'button'); // To prevent submitting form if modal is rendered into form tag
                    closeButton.className = PrkModal.CLOSE_BUTTON_CLASS;
                    closeButton.dataset[PrkModal.ACTION_DATASET] = PrkModal.ACTION_CLOSE;
                    closeButton.innerHTML = PrkModal.CLOSE_ICON;
                    if (this.modal.childNodes.length)
                        this.modal.insertBefore(closeButton, this.modal.firstChild);
                    else
                        this.modal.appendChild(closeButton);
                }
            }

            this.modal.setAttribute('role', 'dialog'); // Ensure role is set to 'dialog'
            document.body.appendChild(this.wrap); // Move wrapperd modal into body to prevent rendering errors
            this.attachEvents();
            this.wrap.setAttribute(PrkModal.IS_HIDDEN_ATTRIBUTE, 'true'); // Hidden by deafault
            this.modal.classList.add(PrkModal.IS_INITIALIZED_CLASS);
        }
    }

    /**
     * Detaches events and removes instance from PrkModal.list
     * @important variable has to be set to null or undefined explicitly
     * @public
     */
    destroy () {
        if (PrkModal.list[this.id]) {
            delete PrkModal.list[this.id];
            this.detachEvents();
            this.modal.classList.remove(PrkModal.IS_INITIALIZED_CLASS);
        }
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isOpen () {
        return !this.isClosed;
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isClosed () {
        return 'true' === this.wrap.getAttribute(PrkModal.IS_HIDDEN_ATTRIBUTE);
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isInitialized () {
        return this.modal.classList.contains(PrkModal.IS_INITIALIZED_CLASS);
    }

    /**
     * @public
     * @returns {string}
     */
    get id () {
        return this.modal.id;
    }

    /**
     * @public
     * @param {string} value
     */
    set id (value) {
        this.modal.id = `${value}`;
    }

    /**
     * @public
     * @returns {boolean}
     */
    get isGeneratedId () {
        const mask = new RegExp(`^${PrkModal.GENERATED_ID}-[\\d]+$`);
        return mask.test(this.id);
    }

    /**
     * Attaches events for modal to function
     * @private
     */
    attachEvents () {
        this.wrap.addEventListener('click', this.eventListeners.onInsideModalClick);
        document.addEventListener('keydown', this.eventListeners.onKeyPress);
        document.addEventListener('onpopstate', this.eventListeners.onHistoryPopState);
    }

    /**
     * Detaches events of the modal
     * @private
     */
    detachEvents () {
        this.wrap.removeEventListener('click', this.eventListeners.onInsideModalClick);
        document.removeEventListener('keydown', this.eventListeners.onKeyPress);
        document.removeEventListener('onpopstate', this.eventListeners.onHistoryPopState);
    }

    /**
     * Returns true if 'ESC' was pressed
     * @private
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    isEscapePressed (event) {
        return event.key === 'Escape' || (event.keyCode || event.which) === 27;
    }

    /**
     * Handles keypress or keydown event
     * @private
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    onKeyPress (event) {
        const { isPersistant, closeOnEscape } = this.options;
        if (isPersistant || !closeOnEscape || !this.isEscapePressed(event))
            return;

        this.close();
    }

    /**
     * Returns true on outside of modal click
     * @private
     * @param {MouseEvent|TouchEvent|PointerEvent} event
     * @returns {boolean}
     */
    isClickedOnBackdrop (event) {
        return event.target === this.wrap;
    }

    isCloseActionAttachedToElement (element) {
        return [PrkModal.ACTION_CLOSE, PrkModal.ACTION_CONFIRM, PrkModal.ACTION_CANCEL].includes(element.dataset[PrkModal.ACTION_DATASET]);
    }

    /**
     * Returns true on click on a [data-prk-modal-action="close"] element
     * @private
     * @param {MouseEvent|TouchEvent|PointerEvent} event
     * @returns {{ result: boolean, action: string|null }}
     */
    isClickedOnCloseButton (event) {
        if (event.target && this.isCloseActionAttachedToElement(event.target))
            return { result: true, action: event.target.dataset[PrkModal.ACTION_DATASET] };

        // If close button contains an icon or text,
        // event.target would be that.
        // Not to depend on jQuery.parents or Dom7.parents
        // check parents of event.target manually.
        let parent = event.target.parentElement;
        while (parent && parent !== this.modal) {
            if (this.isCloseActionAttachedToElement(parent))
                return { result: true, action: parent.dataset[PrkModal.ACTION_DATASET] };
                
            parent = parent.parentElement;
        }

        return { result: false, action: null };
    }

    /**
     * Prevents clicks from propagating outsize the modal,
     * handles closing of modal on backdrop click (if allowed)
     * and handles closing of modal on close button (with [data-prk-modal-action="close"] attribute)
     * @private
     * @param {MouseEvent|TouchEvent|PointerEvent} event 
     */
    onInsideModalClick (event) {
        const { isPersistant, closeOnBackdropClick } = this.options;
        const { result: isClickedOnCloseButton, action } =  this.isClickedOnCloseButton(event);

        if (this.isOpen && (closeOnBackdropClick && this.isClickedOnBackdrop(event) && !isPersistant || isClickedOnCloseButton)) {
            
            // If clicked on close button (with data-prk-modal-action attribute)
            switch (action) {
                case PrkModal.ACTION_CONFIRM:
                    $$(this.modal).trigger(PrkModal.EVENT_CONFIRMATION)
                    break;

                case PrkModal.ACTION_CANCEL:
                    $$(this.modal).trigger(PrkModal.EVENT_CANCELLATION)
                    break;
            }

            this.close();
        }
    }

    /**
     * Handles history onpopstate event
     * @private
     * @param {PopStateEvent} event 
     * @returns {void}
     */
    onHistoryPopState (event) {
        if (this.isGeneratedId) return;
        if (window.location.hash === `#${this.id}`) this.open();
        else this.close();
    }

    /**
     * Opens the modal
     * @public
     * @param {MouseEvent|TouchEvent|PointerEvent|undefined} event
     * @returns {Promise<void}
     */
    async open (event) {
        if (this.isOpen) return;
        this.wrap.setAttribute(PrkModal.IS_HIDDEN_ATTRIBUTE, 'false');

        const { onBeforeOpen, onOpen, useHistory } = this.options;
        try {
            document.body.classList.add(PrkModal.A_MODAL_IS_OPENED_DOCUMENT_CLASS);
            if (onBeforeOpen instanceof Function)
                await new Promise(resolve => onBeforeOpen.call(this, resolve));
        } catch (error) {
            logError(error);
        } finally {
            if (onOpen instanceof Function)
                onOpen.call(this, event);
            
            // Add modalId to url hash
            if (useHistory && window.history && !this.isGeneratedId) {
                if (window.location.hash !== `#${this.id}`) {
                    const url = window.location.href.replace(/([^#]*)#?.*/, `$1#${this.id}`);
                    window.history.pushState({ url }, document.title, url);
                }
            }
        }
    }

    /**
     * Closes the modal
     * @public
     * @param {MouseEvent|TouchEvent|PointerEvent|undefined} event
     * @returns {Promise<void}
     */
    async close (event) {
        if (this.isClosed) return;
        this.wrap.setAttribute(PrkModal.IS_HIDDEN_ATTRIBUTE, 'true');

        const { onBeforeClose, onClose, useHistory } = this.options;
        try {
            if (onBeforeClose instanceof Function)
                await new Promise(resolve => onBeforeClose.call(this, resolve));
        } catch (error) {
            logError(error);
        } finally {
            if (onClose instanceof Function)
                onClose.call(this, event);

            let isAnotherPrkModalOpen = false;

            // Prevent hiding overlay if any PrkModal is open
            for (const modalId in PrkModal.list) {
                if (PrkModal.list[modalId].isOpen)
                    isAnotherPrkModalOpen = true;
            }
            
            // Remove modalId from url hash
            if (useHistory && window.history && !isAnotherPrkModalOpen) {
                if (window.location.hash) {
                    const url = window.location.href.replace(/([^#]*)#?.*/, `$1`);
                    window.history.pushState({ url }, document.title, url);
                }
            }

            // Hide overlay
            if (!isAnotherPrkModalOpen)
                document.body.classList.remove(PrkModal.A_MODAL_IS_OPENED_DOCUMENT_CLASS);
        }
    }

    /**
     * Opens or closes the modal
     * @public
     * @returns {void}
     */
    toggle () {
        if (this.isClosed) this.open();
        else this.close();
    }
}

export default PrkModal;
